import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { CreditCard, Eye, Flag, Lock, UserCheck } from "lucide-react";
import { HEYCATCH_EVENTS, trackOutcome } from "@/lib/heycatch";

/**
 * Trust & Safety - ONLY systems that genuinely exist in this codebase today.
 *
 * WHAT BACKS EACH CLAIM BELOW (checked before writing it)
 *  - "Privacy-conscious profiles": registration takes a username, never a legal
 *    name, address, employer, sobriety date or medical history.
 *  - "Block and report": POST /api/blocks/{id} and POST /api/reports exist and
 *    are free for every account, including free ones.
 *  - "Human moderation": reports land in an admin queue (GET /api/admin/reports)
 *    that a human reviews and resolves (POST /api/admin/reports/{id}/resolve).
 *  - "Card payments handled by Stripe": checkout is Stripe's own hosted session
 *    (stripe.checkout.Session.create, mode="subscription"), so card details are
 *    entered on Stripe and never reach Sponsor Me's servers or database.
 *  - "Sponsors are not vetted": stated plainly, because there is no background
 *    check, identity check or certification anywhere in the product.
 *
 * DO NOT add security certifications, audit badges, encryption grades, payment
 * logos, money-back guarantees or verification claims here. None of those exist.
 */
const ITEMS = [
  {
    icon: Lock,
    title: "Privacy-conscious profiles",
    body: "Signing up takes a display name and an email. We never ask for your full legal name, exact home address, employer, sobriety date or medical history, and you choose what goes on your profile.",
  },
  {
    icon: Flag,
    title: "Block and report, always free",
    body: "Every conversation has Block and Report built in. They work on free accounts too - safety tools are never behind a paywall.",
  },
  {
    icon: Eye,
    title: "Reports reviewed by a human",
    body: "Reports go to a moderation queue that a person reads and acts on. Accounts that break the community rules are suspended or removed.",
  },
  {
    icon: CreditCard,
    title: "Card payments handled by Stripe",
    body: "Checkout happens on Stripe's own hosted page, so your full card details are never entered into Sponsor Me or stored in our database. Browsing is free, and sponsors never pay.",
  },
  {
    icon: UserCheck,
    title: "Honest about vetting",
    body: "Sponsors are volunteers who write their own profiles. Sponsor Me does not background check, identity verify, certify or professionally screen anyone - so read profiles carefully and go at your own pace.",
  },
];

export default function TrustSafety() {
  const ref = useRef(null);
  const sentRef = useRef(false);

  // Fire once per page view, only when the section is genuinely on screen.
  // The ref guard means re-renders, Strict Mode's double effect and repeated
  // scrolling past it cannot send it twice. No properties are attached.
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !sentRef.current) {
          sentRef.current = true;
          trackOutcome(HEYCATCH_EVENTS.TRUST_SAFETY_VIEWED);
          io.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      data-testid="home-trust-safety"
      aria-labelledby="trust-safety-heading"
      className="bg-white border-y border-[#E3DED4]"
    >
      <div className="max-w-7xl mx-auto px-4 py-14 lg:py-16">
        <div className="max-w-3xl">
          <h2
            id="trust-safety-heading"
            data-testid="trust-safety-heading"
            className="font-display text-2xl sm:text-3xl font-bold text-[#1F2A2E]"
          >
            Trust &amp; Safety
          </h2>
          <p className="mt-3 text-[#4B5A60] leading-8">
            What Sponsor Me actually does - and what it deliberately does not claim to do.
          </p>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {ITEMS.map((item) => (
            <article
              key={item.title}
              data-testid={`trust-item-${item.title.split(" ")[0].toLowerCase()}`}
              className="sm-card p-5 bg-[#FBF9F5] flex flex-col gap-3"
            >
              <span className="w-10 h-10 rounded-xl bg-[#D7EEF0] text-[#1F6F78] flex items-center justify-center">
                <item.icon className="w-5 h-5" aria-hidden="true" />
              </span>
              <h3 className="font-display text-base font-bold text-[#1F2A2E]">{item.title}</h3>
              <p className="text-sm text-[#4B5A60] leading-6">{item.body}</p>
            </article>
          ))}
        </div>

        <p className="mt-6 text-sm text-[#4B5A60] leading-7 max-w-3xl">
          Read the full detail in our{" "}
          <Link to="/privacy" className="font-semibold text-[#1F6F78] hover:underline">
            Privacy Policy
          </Link>{" "}
          and{" "}
          <Link to="/terms" className="font-semibold text-[#1F6F78] hover:underline">
            Terms of Service
          </Link>
          , or see the crisis lines on our{" "}
          <Link to="/resources" className="font-semibold text-[#1F6F78] hover:underline">
            Resources page
          </Link>
          .
        </p>
      </div>
    </section>
  );
}
